import { useEffect, useMemo, useState, type DependencyList, type FormEvent } from "react";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  Download,
  FileArchive,
  FileText,
  Gauge,
  KeyRound,
  LockKeyhole,
  Loader2,
  LogOut,
  MessageSquare,
  RefreshCw,
  ScanText,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  Upload
} from "lucide-react";
import { client, type AdminProfile, type AuditLog, type Company, type Dashboard, type ExportRecord, type Invoice, type Settings as SettingsType, type TotpSetupStart } from "./api/client";

type Tab = "dashboard" | "invoices" | "review" | "companies" | "ocr" | "bailian" | "cos" | "wecom" | "security" | "exports" | "logs";
type InvoiceAction = "process" | "ocr" | "classify" | "image" | "remove";

const tabMeta: Record<Tab, { label: string; icon: typeof Gauge; module: string }> = {
  dashboard: { label: "发票总览", icon: Gauge, module: "发票管理" },
  invoices: { label: "发票列表", icon: FileText, module: "发票管理" },
  review: { label: "待确认", icon: AlertTriangle, module: "发票管理" },
  companies: { label: "参数配置", icon: Building2, module: "发票管理" },
  ocr: { label: "阿里云 OCR", icon: ScanText, module: "智能服务" },
  bailian: { label: "百炼配置", icon: KeyRound, module: "智能服务" },
  exports: { label: "月度导出", icon: FileArchive, module: "发票管理" },
  cos: { label: "COS 配置", icon: ShieldCheck, module: "系统设置" },
  wecom: { label: "企业微信", icon: MessageSquare, module: "系统设置" },
  security: { label: "修改密码", icon: LockKeyhole, module: "系统设置" },
  logs: { label: "系统日志", icon: Settings, module: "系统设置" }
};

const navSections: Array<{
  id: string;
  label: string;
  items: Array<{ key: Tab; label: string; icon: typeof Gauge }>;
}> = [
  {
    id: "invoice",
    label: "发票管理",
    items: [
      { key: "dashboard", label: tabMeta.dashboard.label, icon: tabMeta.dashboard.icon },
      { key: "invoices", label: tabMeta.invoices.label, icon: tabMeta.invoices.icon },
      { key: "review", label: tabMeta.review.label, icon: tabMeta.review.icon },
      { key: "exports", label: tabMeta.exports.label, icon: tabMeta.exports.icon },
      { key: "companies", label: tabMeta.companies.label, icon: tabMeta.companies.icon }
    ]
  },
  {
    id: "intelligence",
    label: "智能服务",
    items: [
      { key: "ocr", label: tabMeta.ocr.label, icon: tabMeta.ocr.icon },
      { key: "bailian", label: tabMeta.bailian.label, icon: tabMeta.bailian.icon }
    ]
  },
  {
    id: "system",
    label: "系统设置",
    items: [
      { key: "cos", label: tabMeta.cos.label, icon: tabMeta.cos.icon },
      { key: "wecom", label: tabMeta.wecom.label, icon: tabMeta.wecom.icon },
      { key: "security", label: tabMeta.security.label, icon: tabMeta.security.icon },
      { key: "logs", label: tabMeta.logs.label, icon: tabMeta.logs.icon }
    ]
  }
];

export function App() {
  const [admin, setAdmin] = useState<AdminProfile | null | undefined>(undefined);
  const [tab, setTab] = useState<Tab>("dashboard");
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    invoice: true,
    intelligence: true,
    system: true
  });
  const [toast, setToast] = useState("");

  useEffect(() => {
    client.me()
      .then((result) => setAdmin(result.authenticated && result.admin ? result.admin : null))
      .catch(() => setAdmin(null));
  }, []);

  useEffect(() => {
    function handleSessionExpired() {
      setAdmin(null);
    }
    function refreshAdminProfile() {
      client.me()
        .then((result) => setAdmin(result.authenticated && result.admin ? result.admin : null))
        .catch(() => setAdmin(null));
    }
    window.addEventListener("admin-session-expired", handleSessionExpired);
    window.addEventListener("admin-profile-refresh", refreshAdminProfile);
    return () => {
      window.removeEventListener("admin-session-expired", handleSessionExpired);
      window.removeEventListener("admin-profile-refresh", refreshAdminProfile);
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (admin === undefined) {
    return <LoadingScreen />;
  }

  if (!admin) {
    return <LoginPage onLogin={setAdmin} />;
  }

  const effectiveTab: Tab = admin.totpSetupRequired ? "security" : tab;
  const ActiveIcon = tabMeta[effectiveTab].icon;
  const visibleNavSections = admin.totpSetupRequired
    ? [{ id: "system", label: "系统设置", items: [{ key: "security" as Tab, label: tabMeta.security.label, icon: tabMeta.security.icon }] }]
    : navSections;

  function toggleSection(id: string) {
    setExpandedSections((current) => ({ ...current, [id]: !current[id] }));
  }

  function openExports(month: string) {
    setExportMonth(month);
    setToast("");
    setTab("exports");
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">PH</div>
          <div>
            <strong>个人工作中心</strong>
            <span>{tabMeta[effectiveTab].module}</span>
          </div>
        </div>
        <nav className="nav-list">
          {visibleNavSections.map((section) => {
            const expanded = expandedSections[section.id];
            return (
              <div className="nav-section" key={section.id}>
                <button className="nav-section-toggle" onClick={() => toggleSection(section.id)}>
                  {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span>{section.label}</span>
                </button>
                {expanded && (
                  <div className="nav-sublist">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button key={item.key} className={effectiveTab === item.key ? "active" : ""} onClick={() => setTab(item.key)}>
                          <Icon size={17} />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <button className="logout" onClick={() => client.logout().then(() => setAdmin(null))}>
          <LogOut size={18} />
          <span>退出</span>
        </button>
      </aside>
      <main className="main">
        <header className="topbar">
          <div>
            <div className="eyebrow">{environmentLabel()}</div>
            <h1><ActiveIcon size={26} />{tabMeta[effectiveTab].label}</h1>
          </div>
          <div className="admin-chip">
            <ShieldCheck size={16} />
            <span>{admin.username}</span>
            <strong>{admin.roleName || "超级管理员"}</strong>
          </div>
          {toast && <div className="toast">{toast}</div>}
        </header>
        <View tab={effectiveTab} setToast={setToast} exportMonth={exportMonth} openExports={openExports} />
      </main>
    </div>
  );
}

function View({
  tab,
  setToast,
  exportMonth,
  openExports
}: {
  tab: Tab;
  setToast: (value: string) => void;
  exportMonth: string;
  openExports: (month: string) => void;
}) {
  if (tab === "dashboard") return <DashboardPage />;
  if (tab === "invoices") return <InvoicesPage setToast={setToast} onExportMonth={openExports} />;
  if (tab === "review") return <InvoicesPage status="review" setToast={setToast} onExportMonth={openExports} showMonthFilter={false} />;
  if (tab === "companies") return <CompaniesPage setToast={setToast} />;
  if (tab === "ocr") return <InvoiceOcrPage setToast={setToast} />;
  if (tab === "bailian") return <BailianPage setToast={setToast} />;
  if (tab === "cos") return <CosPage setToast={setToast} />;
  if (tab === "wecom") return <WecomPage />;
  if (tab === "security") return <SecurityPage setToast={setToast} />;
  if (tab === "exports") return <ExportsPage setToast={setToast} initialMonth={exportMonth} />;
  return <LogsPage />;
}

function LoginPage({ onLogin }: { onLogin: (admin: AdminProfile) => void }) {
  const [username, setUsername] = useState("super_admin");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (challengeId) {
        const result = await client.totpLogin(challengeId, totpCode);
        onLogin(result.admin);
        return;
      }
      const result = await client.login(username, password);
      if (result.requiresTotp) {
        setChallengeId(result.challengeId);
        setTotpCode("");
        setError("");
        return;
      }
      onLogin(result.admin);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  function backToPassword() {
    setChallengeId("");
    setTotpCode("");
    setError("");
  }

  return (
    <div className="login-page">
      <form className="login-panel" onSubmit={submit}>
        <div className="brand-mark large">PH</div>
        <h1>个人工作中心</h1>
        {!challengeId ? (
          <>
            <label>
              超级管理员账号
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoFocus />
            </label>
            <label>
              管理员密码
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" />
            </label>
          </>
        ) : (
          <>
            <div className="security-note">
              <strong>动态码验证</strong>
              <span>请输入 Google Authenticator 中的 6 位动态码，也可以输入一次性恢复码。</span>
            </div>
            <label>
              动态码 / 恢复码
              <input value={totpCode} onChange={(event) => setTotpCode(event.target.value)} autoFocus inputMode="numeric" autoComplete="one-time-code" />
            </label>
          </>
        )}
        {error && <div className="form-error">{error}</div>}
        <button className="primary" disabled={loading}>
          {loading ? <Loader2 className="spin" size={18} /> : <KeyRound size={18} />}
          {challengeId ? "验证并登录" : "登录"}
        </button>
        {challengeId && <button type="button" className="secondary full-button" onClick={backToPassword}>返回账号密码登录</button>}
      </form>
    </div>
  );
}

function DashboardPage() {
  const { data, reload, loading } = useLoad(() => client.dashboard(), []);
  if (loading || !data) return <PanelLoading />;

  const countMap = new Map(data.counts.map((item) => [item.status, item.count]));
  const directionMap = new Map((data.monthlyByDirection || []).map((item) => [item.invoice_direction, item]));
  const documentTypeMap = new Map((data.monthlyByDocumentType || []).map((item) => [item.document_type, item]));
  const input = directionMap.get("input");
  const output = directionMap.get("output");
  const invoiceDocs = documentTypeMap.get("invoice");
  const vouchers = documentTypeMap.get("voucher");
  return (
    <section className="content-grid">
      <Metric title="本月票据" value={data.monthly.count} />
      <Metric title="本月金额" value={money(data.monthly.total)} />
      <Metric title="标准发票" value={invoiceDocs?.count || 0} />
      <Metric title="非发票凭证" value={vouchers?.count || 0} />
      <Metric title="本月进项" value={`${input?.count || 0} / ${money(Number(input?.total || 0))}`} />
      <Metric title="本月销项" value={`${output?.count || 0} / ${money(Number(output?.total || 0))}`} />
      <Metric title="待确认" value={(countMap.get("needs_review") || 0) + (countMap.get("company_unassigned") || 0) + (countMap.get("ocr_failed") || 0) + (countMap.get("ocr_pending") || 0)} tone="warn" />
      <div className="panel wide">
        <div className="panel-head">
          <h2>公司汇总</h2>
          <button className="icon-button" title="刷新" aria-label="刷新" onClick={reload}><RefreshCw size={17} /></button>
        </div>
        <table>
          <thead><tr><th>公司</th><th>数量</th><th>金额</th><th>进项</th><th>销项</th></tr></thead>
          <tbody>
            {data.companies.map((company) => (
              <tr key={company.name}>
                <td>{company.name}</td>
                <td>{company.count}</td>
                <td>{money(company.total)}</td>
                <td>{company.input_count || 0} / {money(Number(company.input_total || 0))}</td>
                <td>{company.output_count || 0} / {money(Number(company.output_total || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel">
        <h2>系统状态</h2>
        <StatusLine label="存储模式" value={data.storageMode === "cos" ? "腾讯云 COS" : "本地模拟"} />
        <StatusLine label="保留天数" value={`${data.retentionDays} 天`} />
      </div>
    </section>
  );
}

function InvoicesPage({
  status,
  setToast,
  onExportMonth,
  showMonthFilter = true
}: {
  status?: string;
  setToast: (value: string) => void;
  onExportMonth: (month: string) => void;
  showMonthFilter?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [previewing, setPreviewing] = useState<Invoice | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyAction, setBusyAction] = useState<{ invoiceId: number; action: InvoiceAction } | null>(null);
  const query = useMemo(() => {
    const q: Record<string, string> = {};
    if (status) q.status = status;
    if (showMonthFilter && month) q.month = month;
    if (search) q.search = search;
    return q;
  }, [status, month, search, showMonthFilter]);
  const { data, reload, loading } = useLoad(() => client.invoices(query), [query]);
  const hasActiveJobs = useMemo(() => Boolean(data?.items.some((invoice) => {
    const isImage = invoice.mime_type?.startsWith("image/");
    const imageStatus = invoice.image_process_status;
    return invoice.status === "ocr_pending" || (isImage && (imageStatus === "pending" || imageStatus === "processing"));
  })), [data]);

  useEffect(() => {
    if (!hasActiveJobs) return;
    const timer = window.setInterval(() => reload(), 5000);
    return () => window.clearInterval(timer);
  }, [hasActiveJobs, reload]);

  async function upload(file?: File) {
    if (!file || uploading) return;
    setUploading(true);
    try {
      await client.uploadInvoice(file);
      setToast("上传完成，已进入识别队列");
      reload();
    } catch (error) {
      setToast(error instanceof Error ? error.message : "上传失败");
    } finally {
      setUploading(false);
    }
  }

  async function removeInvoice(invoice: Invoice) {
    if (!window.confirm(`确定删除发票 #${invoice.id} 吗？删除后不会进入列表和月度导出。`)) return;
    const note = invoice.manual_note ? `${invoice.manual_note}\n管理员删除/忽略` : "管理员删除/忽略";
    await runInvoiceAction(invoice, "remove", () => client.updateInvoice(invoice.id, { status: "ignored", manual_note: note }), () => `#${invoice.id} 已删除`);
  }

  async function retryOcr(invoice: Invoice) {
    await runInvoiceAction(invoice, "ocr", () => client.retryInvoiceOcr(invoice.id), (updated) => (
      `#${invoice.id} 已进入重新识别队列，当前状态：${invoiceStatusLabel(updated.status)}`
    ));
  }

  async function retryClassification(invoice: Invoice) {
    await runInvoiceAction(invoice, "classify", () => client.retryInvoiceClassification(invoice.id), (updated) => (
      `#${invoice.id} 已重新归类：${updated.company_short_name || updated.company_name || "未分配"} / ${directionLabel(updated.invoice_direction)} / ${invoiceStatusLabel(updated.status)}`
    ));
  }

  async function retryImage(invoice: Invoice) {
    await runInvoiceAction(invoice, "image", () => client.retryInvoiceImage(invoice.id), (updated) => (
      `#${invoice.id} 已进入裁切队列，当前裁切状态：${imageProcessLabel(updated)}`
    ));
  }

  async function reprocess(invoice: Invoice) {
    await runInvoiceAction(invoice, "process", () => client.reprocessInvoice(invoice.id), (result) => (
      `#${invoice.id} ${result.message}`
    ));
  }

  async function runInvoiceAction<T>(invoice: Invoice, action: InvoiceAction, work: () => Promise<T>, successMessage: (updated: T) => string) {
    if (busyAction) return;
    setBusyAction({ invoiceId: invoice.id, action });
    setToast("");
    try {
      const updated = await work();
      setToast(successMessage(updated));
      reload();
    } catch (error) {
      setToast(error instanceof Error ? error.message : "操作失败");
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <section className="panel full">
      <div className="panel-head toolbar">
        <div className="filter-group">
          {showMonthFilter && <span className="filter-label">开票月份</span>}
          <label><Search size={16} /><input placeholder="搜索" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          {showMonthFilter && <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />}
        </div>
        <label className={`upload-button${uploading ? " disabled" : ""}`} aria-disabled={uploading}>
          {uploading ? <Loader2 className="spin" size={17} /> : <Upload size={17} />}
          {uploading ? "上传中" : "管理员补录"}
          <input
            type="file"
            accept="image/*,application/pdf"
            disabled={uploading}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              void upload(file);
            }}
          />
        </label>
        {showMonthFilter && <button type="button" className="secondary" onClick={() => onExportMonth(month)}><FileArchive size={17} />导出本月会计包</button>}
      </div>
      {loading || !data ? <PanelLoading /> : (
        <table>
          <thead>
            <tr>
              <th>ID</th><th>来源</th><th>提交人</th><th>公司</th><th>类型</th><th>方向</th><th>日期/月</th><th>号码</th><th>金额</th><th>状态</th><th>裁切</th><th>保留至</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr>
                <td className="empty-cell" colSpan={13}>{showMonthFilter ? `${month} 暂无发票` : "暂无待确认发票"}</td>
              </tr>
            ) : data.items.map((invoice) => {
              const actionBusy = (action: InvoiceAction) => busyAction?.invoiceId === invoice.id && busyAction.action === action;
              const actionsDisabled = Boolean(busyAction);
              return (
                <tr key={invoice.id}>
                  <td>#{invoice.id}</td>
                  <td>{sourceLabel(invoice.source)}</td>
                  <td>{invoice.submitter_name || invoice.submitter_external_user_id || "-"}</td>
                  <td>{invoice.company_short_name || invoice.company_name || "未分配"}</td>
                  <td>{documentTypeLabel(invoice)}</td>
                  <td>{directionLabel(invoice.invoice_direction)}</td>
                  <td>{invoice.invoice_date || invoice.voucher_period_month || "-"}</td>
                  <td>{invoice.invoice_number || "-"}</td>
                  <td>{invoice.total_amount ? money(invoice.total_amount) : "-"}</td>
                  <td><Badge value={invoice.status} /></td>
                  <td>{imageProcessLabel(invoice)}</td>
                  <td>{shortDate(invoice.retention_delete_after)}</td>
                  <td className="row-actions">
                    <button type="button" className="table-action" title="预览这张原始发票" aria-label="预览这张原始发票" onClick={() => setPreviewing(invoice)}><Eye size={16} />预览</button>
                    <button type="button" className="table-action" title="编辑这张发票" aria-label="编辑这张发票" onClick={() => setEditing(invoice)}><Settings size={16} />编辑</button>
                    <button type="button" className="table-action primary-action" title="自动判断重识别或重归类" aria-label="自动重新处理这张票据" disabled={actionsDisabled} onClick={() => reprocess(invoice)}>
                      {actionBusy("process") ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />}重新处理
                    </button>
                    <button type="button" className="table-action" title="只按购买方/销售方重新归类，不重新 OCR" aria-label="只重新归类" disabled={actionsDisabled} onClick={() => retryClassification(invoice)}>
                      {actionBusy("classify") ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />}只归类
                    </button>
                    <button type="button" className="table-action" title="只重新 OCR 识别，会消耗一次识别流程" aria-label="只重新 OCR 识别" disabled={actionsDisabled} onClick={() => retryOcr(invoice)}>
                      {actionBusy("ocr") ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />}只识别
                    </button>
                    <button type="button" className="table-action" title="只重新裁切图片，不影响 OCR 字段" aria-label="只重新裁切图片" disabled={actionsDisabled} onClick={() => retryImage(invoice)}>
                      {actionBusy("image") ? <Loader2 className="spin" size={16} /> : <RefreshCw size={16} />}只裁切
                    </button>
                    <a className="table-action" title="下载这张原始发票" aria-label="下载这张原始发票" href={client.invoiceFileUrl(invoice.id, invoice.original_filename)}><Download size={16} />下载原票</a>
                    <button type="button" className="table-action danger-action" title="删除这张发票" aria-label="删除这张发票" disabled={actionsDisabled} onClick={() => removeInvoice(invoice)}>
                      {actionBusy("remove") ? <Loader2 className="spin" size={16} /> : <Trash2 size={16} />}删除
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {previewing && <InvoicePreview invoice={previewing} onClose={() => setPreviewing(null)} />}
      {editing && <InvoiceEditor invoice={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </section>
  );
}

function InvoicePreview({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const url = client.invoicePreviewUrl(invoice.id, invoice.original_filename);
  const isPdf = invoice.mime_type === "application/pdf" || invoice.original_filename?.toLowerCase().endsWith(".pdf");
  return (
    <div className="modal-backdrop">
      <div className="modal preview-modal">
        <div className="panel-head">
          <h2>原票预览 #{invoice.id}</h2>
          <button type="button" onClick={onClose}>关闭</button>
        </div>
        <div className="preview-stage">
          {isPdf ? (
            <iframe title={`发票 ${invoice.id} 预览`} src={url} />
          ) : (
            <img alt={`发票 ${invoice.id} 预览`} src={url} />
          )}
        </div>
        <div className="modal-actions">
          <a className="table-action" href={client.invoiceFileUrl(invoice.id, invoice.original_filename)}><Download size={16} />下载原票</a>
        </div>
      </div>
    </div>
  );
}

function InvoiceEditor({ invoice, onClose, onSaved }: { invoice: Invoice; onClose: () => void; onSaved: () => void }) {
  const { data: companies } = useLoad(() => client.companies(), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_id: invoice.company_id || "",
    document_type: invoice.document_type || "invoice",
    voucher_type: invoice.voucher_type || "unknown",
    voucher_period_month: invoice.voucher_period_month || "",
    invoice_direction: invoice.invoice_direction || "unknown",
    invoice_date: invoice.invoice_date || "",
    invoice_number: invoice.invoice_number || "",
    buyer_name: invoice.buyer_name || "",
    total_amount: invoice.total_amount || "",
    status: invoice.status,
    manual_note: invoice.manual_note || ""
  });

  async function save(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await client.updateInvoice(invoice.id, {
        ...form,
        company_id: form.company_id ? Number(form.company_id) : null,
        total_amount: form.total_amount ? Number(form.total_amount) : null
      } as Partial<Invoice>);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={save}>
        <h2>发票 #{invoice.id}</h2>
        <label>公司
          <select value={form.company_id} onChange={(event) => setForm({ ...form, company_id: event.target.value })}>
            <option value="">未分配</option>
            {companies?.map((company) => <option key={company.id} value={company.id}>{company.short_name}</option>)}
          </select>
        </label>
        <label>文档类型
          <select value={form.document_type} onChange={(event) => setForm({ ...form, document_type: event.target.value })}>
            <option value="invoice">标准发票</option>
            <option value="voucher">非发票凭证</option>
          </select>
        </label>
        <label>凭证类型
          <select value={form.voucher_type} onChange={(event) => setForm({ ...form, voucher_type: event.target.value })}>
            <option value="unknown">未判断</option>
            <option value="social_security">社保</option>
            <option value="medical_insurance">医保</option>
            <option value="payment_receipt">回单/缴费凭证</option>
            <option value="other_voucher">其他凭证</option>
          </select>
        </label>
        <label>凭证所属月<input type="month" value={form.voucher_period_month} onChange={(event) => setForm({ ...form, voucher_period_month: event.target.value })} /></label>
        <label>方向
          <select value={form.invoice_direction} onChange={(event) => setForm({ ...form, invoice_direction: event.target.value })}>
            <option value="unknown">未判断</option>
            <option value="input">进项</option>
            <option value="output">销项</option>
          </select>
        </label>
        <label>开票日期<input value={form.invoice_date} onChange={(event) => setForm({ ...form, invoice_date: event.target.value })} /></label>
        <label>发票号码<input value={form.invoice_number} onChange={(event) => setForm({ ...form, invoice_number: event.target.value })} /></label>
        <label>购买方<input value={form.buyer_name} onChange={(event) => setForm({ ...form, buyer_name: event.target.value })} /></label>
        <label>价税合计<input value={form.total_amount} onChange={(event) => setForm({ ...form, total_amount: event.target.value })} /></label>
        <label>状态
          <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
            {["needs_review", "company_unassigned", "ready", "ignored"].map((item) => (
              <option key={item} value={item}>{invoiceStatusLabel(item)}</option>
            ))}
          </select>
        </label>
        <label>备注<textarea value={form.manual_note} onChange={(event) => setForm({ ...form, manual_note: event.target.value })} /></label>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" disabled={saving} onClick={onClose}>取消</button>
          <button className="primary" disabled={saving}>
            {saving ? <Loader2 className="spin" size={17} /> : <CheckCircle2 size={17} />}保存
          </button>
        </div>
      </form>
    </div>
  );
}

function CompaniesPage({ setToast }: { setToast: (value: string) => void }) {
  const { data, reload, loading } = useLoad(() => client.companies(), []);
  const { data: settings, reload: reloadSettings } = useLoad(() => client.settings(), []);
  const [editing, setEditing] = useState<Company | null>(null);
  return (
    <section className="content-stack">
      {settings ? <RetentionPanel settings={settings} setToast={setToast} onSaved={reloadSettings} /> : <PanelLoading />}
      <div className="panel full">
        <div className="panel-head">
          <h2>公司归类</h2>
          <button className="primary" onClick={() => setEditing({ id: 0, name: "", short_name: "", tax_id: "", aliases: "[]", enabled: 1 })}>新增</button>
        </div>
        {loading ? <PanelLoading /> : (
          <table>
            <thead><tr><th>公司</th><th>简称</th><th>税号</th><th>别名</th><th>状态</th><th></th></tr></thead>
            <tbody>
              {data?.map((company) => (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td>{company.short_name}</td>
                  <td>{company.tax_id || "未填写"}</td>
                  <td>{parseAliases(company.aliases).join("、") || "-"}</td>
                  <td>{company.enabled ? "启用" : "停用"}</td>
                  <td><button className="icon-button" title="编辑" aria-label="编辑" onClick={() => setEditing(company)}><Settings size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <CompanyEditor company={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); setToast("公司已保存"); }} />}
    </section>
  );
}

function RetentionPanel({ settings, setToast, onSaved }: { settings: SettingsType; setToast: (value: string) => void; onSaved: () => void }) {
  const [invoiceFileRetentionDays, setInvoiceFileRetentionDays] = useState(settings.retention.invoiceFileRetentionDays);
  const [exportRetentionDays, setExportRetentionDays] = useState(settings.retention.exportRetentionDays);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    setInvoiceFileRetentionDays(settings.retention.invoiceFileRetentionDays);
    setExportRetentionDays(settings.retention.exportRetentionDays);
  }, [settings]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await client.updateRetention({ invoiceFileRetentionDays, exportRetentionDays });
      setToast("保留时间已保存");
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function cleanup() {
    setCleaning(true);
    try {
      const result = await client.runRetention();
      setToast(`已清理 ${result.deletedFiles} 个原票文件、${result.deletedExports} 个会计包`);
    } finally {
      setCleaning(false);
    }
  }

  return (
    <section className="panel full">
      <div className="panel-head">
        <h2>存储参数</h2>
        <button className="secondary" disabled={cleaning} onClick={cleanup}>
          {cleaning ? <Loader2 className="spin" size={17} /> : <Trash2 size={17} />}
          立即清理到期文件
        </button>
      </div>
      <form className="settings-form retention-form" onSubmit={save}>
        <label>原始发票保留天数<input type="number" min="1" max="3650" value={invoiceFileRetentionDays} onChange={(event) => setInvoiceFileRetentionDays(Number(event.target.value) || 1)} /></label>
        <label>会计包保留天数<input type="number" min="1" max="3650" value={exportRetentionDays} onChange={(event) => setExportRetentionDays(Number(event.target.value) || 1)} /></label>
        <StatusLine label="自动清理" value={settings.retention.cleanupFrequency === "daily" ? "每天一次" : settings.retention.cleanupFrequency} />
        <StatusLine label="OCR 处理" value="每分钟检查待识别任务" />
        <p className="form-hint">保留天数对后续新上传发票和新生成会计包生效；已生成记录按各自“保留至”清理。OCR 每分钟只查本地待处理队列，没有新发票时不会调用阿里云；到期清理每天一次，也可手动执行。</p>
        <button className="primary" disabled={saving}>
          {saving ? <Loader2 className="spin" size={17} /> : <CheckCircle2 size={17} />}
          保存参数
        </button>
      </form>
    </section>
  );
}

function CompanyEditor({ company, onClose, onSaved }: { company: Company; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: company.name,
    short_name: company.short_name,
    tax_id: company.tax_id || "",
    aliases: parseAliases(company.aliases).join("\n"),
    enabled: Boolean(company.enabled)
  });
  async function save(event: FormEvent) {
    event.preventDefault();
    await client.saveCompany({
      ...form,
      aliases: form.aliases.split(/\n|,/).map((item) => item.trim()).filter(Boolean)
    }, company.id || undefined);
    onSaved();
  }
  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={save}>
        <h2>{company.id ? "编辑公司" : "新增公司"}</h2>
        <label>公司全称<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label>简称<input value={form.short_name} onChange={(event) => setForm({ ...form, short_name: event.target.value })} /></label>
        <label>税号<input value={form.tax_id} onChange={(event) => setForm({ ...form, tax_id: event.target.value })} /></label>
        <label>别名<textarea value={form.aliases} onChange={(event) => setForm({ ...form, aliases: event.target.value })} /></label>
        <label className="checkline"><input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} />启用</label>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>取消</button>
          <button className="primary">保存</button>
        </div>
      </form>
    </div>
  );
}

function InvoiceOcrPage({ setToast }: { setToast: (value: string) => void }) {
  const { data, reload, loading } = useLoad(() => client.settings(), []);
  const [accessKeyId, setAccessKeyId] = useState("");
  const [accessKeySecret, setAccessKeySecret] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [region, setRegion] = useState("");
  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    if (data) {
      setEndpoint(data.invoiceOcr.endpoint || "");
      setRegion(data.invoiceOcr.region || "");
      setPageNo(data.invoiceOcr.pageNo || 1);
    }
  }, [data]);

  async function save(event: FormEvent) {
    event.preventDefault();
    await client.updateInvoiceOcr({
      provider: "aliyun_invoice",
      endpoint,
      region,
      pageNo,
      accessKeyId: accessKeyId || undefined,
      accessKeySecret: accessKeySecret || undefined
    });
    setAccessKeyId("");
    setAccessKeySecret("");
    setToast("阿里云 OCR 配置已保存");
    reload();
  }

  if (loading || !data) return <PanelLoading />;
  return (
    <section className="panel narrow">
      <form className="settings-form" onSubmit={save}>
        <StatusLine label="AccessKey" value={data.invoiceOcr.accessKeyConfigured ? "已配置" : "未配置"} />
        <StatusLine label="识别接口" value="RecognizeInvoice 增值税发票识别" />
        <label>Endpoint<input value={endpoint} onChange={(event) => setEndpoint(event.target.value)} /></label>
        <label>Region<input value={region} onChange={(event) => setRegion(event.target.value)} /></label>
        <label>PDF 页码<input type="number" min="1" value={pageNo} onChange={(event) => setPageNo(Number(event.target.value) || 1)} /></label>
        <label>AccessKeyId<input type="password" value={accessKeyId} onChange={(event) => setAccessKeyId(event.target.value)} placeholder="留空表示不修改已有密钥" /></label>
        <label>AccessKeySecret<input type="password" value={accessKeySecret} onChange={(event) => setAccessKeySecret(event.target.value)} placeholder="留空表示不修改已有密钥" /></label>
        <p className="form-hint">发票识别使用阿里云 OCR 专用接口；AccessKey 不会明文回显。</p>
        <button className="primary"><ScanText size={17} />保存</button>
      </form>
    </section>
  );
}

function BailianPage({ setToast }: { setToast: (value: string) => void }) {
  const { data, reload, loading } = useLoad(() => client.settings(), []);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  useEffect(() => {
    if (data) {
      setModel(data.bailian.model || "");
      setBaseUrl(data.bailian.baseUrl || "");
    }
  }, [data]);

  async function save(event: FormEvent) {
    event.preventDefault();
    await client.updateBailian({ provider: "bailian", baseUrl, model, apiKey: apiKey || undefined });
    setApiKey("");
    setToast("百炼配置已保存");
    reload();
  }

  if (loading || !data) return <PanelLoading />;
  return (
    <section className="panel narrow">
      <form className="settings-form" onSubmit={save}>
        <StatusLine label="API Key" value={data.bailian.apiKeyConfigured ? "已配置" : "未配置"} />
        <label>Base URL<input value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} /></label>
        <label>默认模型<input value={model} onChange={(event) => setModel(event.target.value)} /></label>
        <label>API Key<input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="留空表示不修改已有密钥" /></label>
        <p className="form-hint">百炼配置预留给后续智能分析、文案、资料整理等功能复用；发票 OCR 不走这里。</p>
        <button className="primary"><KeyRound size={17} />保存</button>
      </form>
    </section>
  );
}

function CosPage({ setToast }: { setToast: (value: string) => void }) {
  const { data } = useLoad(() => client.settings(), []);
  if (!data) return <PanelLoading />;
  return (
    <section className="content-grid">
      <div className="panel">
        <h2>Bucket</h2>
        <StatusLine label="名称" value={data.cos.bucket} />
        <StatusLine label="地域" value={data.cos.region} />
        <StatusLine label="前缀" value={data.cos.prefixBase} />
        <StatusLine label="密钥" value={data.cos.credentialsConfigured ? "已配置" : "本地模拟"} />
      </div>
      <div className="panel">
        <h2>访问策略</h2>
        <StatusLine label="读写权限" value={data.cos.privateReadWrite ? "私有" : "公开"} />
        <StatusLine label="下载方式" value="后端代理/签名 URL" />
        <p className="form-hint">发票保留天数和会计包保留天数在“发票管理 - 参数配置”中设置。</p>
      </div>
    </section>
  );
}

function WecomPage() {
  const { data, loading } = useLoad(() => client.settings(), []);
  if (loading || !data) return <PanelLoading />;
  return (
    <section className="content-grid">
      <div className="panel wide">
        <h2>接收消息</h2>
        <StatusLine label="回调地址" value={data.wecom.callbackUrl} />
        <StatusLine label="整体状态" value={data.wecom.configured ? "已配置" : "未配置"} />
      </div>
      <div className="panel">
        <h2>环境变量</h2>
        <StatusLine label="CorpID" value={data.wecom.corpIdConfigured ? "已配置" : "未配置"} />
        <StatusLine label="AgentID" value={data.wecom.agentIdConfigured ? "已配置" : "未配置"} />
        <StatusLine label="Secret" value={data.wecom.secretConfigured ? "已配置" : "未配置"} />
        <StatusLine label="Token" value={data.wecom.tokenConfigured ? "已配置" : "未配置"} />
        <StatusLine label="AESKey" value={data.wecom.aesKeyConfigured ? "已配置" : "未配置"} />
      </div>
      <div className="panel">
        <h2>收票范围</h2>
        <StatusLine label="图片/截图" value="企业微信主入口" />
        <StatusLine label="PDF" value="企业微信路线已冻结" />
        <StatusLine label="OFD" value="暂缓" />
      </div>
    </section>
  );
}

function SecurityPage({ setToast }: { setToast: (value: string) => void }) {
  const { data, reload, loading } = useLoad(() => client.settings(), []);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("两次输入的新密码不一致");
      return;
    }
    setSaving(true);
    try {
      await client.changePassword({ currentPassword, newPassword, confirmPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setToast("管理员密码已修改");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "修改失败");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !data) return <PanelLoading />;
  return (
    <section className="content-stack security-stack">
      <section className="panel narrow">
        <div className="panel-head">
          <h2>管理员密码设置</h2>
        </div>
        <form className="settings-form" onSubmit={save}>
          <StatusLine label="当前账号" value={data.security.username || "-"} />
          <StatusLine label="角色" value={data.security.roleName || "超级管理员"} />
          <StatusLine label="管理员密码" value={data.security.passwordConfigured ? "已配置" : "未配置"} />
          <StatusLine label="最近修改" value={shortDate(data.security.passwordUpdatedAt)} />
          <label>当前密码<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
          <label>新密码<input type="password" autoComplete="new-password" minLength={10} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
          <label>确认新密码<input type="password" autoComplete="new-password" minLength={10} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></label>
          {error && <div className="form-error">{error}</div>}
          <p className="form-hint">新密码至少 10 位，只保存加密 hash；修改后其他已登录会话会失效。</p>
          <button className="primary" disabled={saving}>
            {saving ? <Loader2 className="spin" size={17} /> : <LockKeyhole size={17} />}
            保存新密码
          </button>
        </form>
      </section>
      <TotpSettingsPanel
        security={data.security}
        reload={reload}
        setToast={setToast}
      />
    </section>
  );
}

function TotpSettingsPanel({
  security,
  reload,
  setToast
}: {
  security: SettingsType["security"];
  reload: () => void;
  setToast: (value: string) => void;
}) {
  const [setup, setSetup] = useState<TotpSetupStart | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [totpError, setTotpError] = useState("");
  const [busy, setBusy] = useState("");
  const [regenerateForm, setRegenerateForm] = useState({ currentPassword: "", code: "" });
  const [disableForm, setDisableForm] = useState({ currentPassword: "", code: "" });

  async function startSetup() {
    setBusy("setup");
    setTotpError("");
    setRecoveryCodes([]);
    try {
      setSetup(await client.startTotpSetup());
      setToast("动态码密钥已生成");
    } catch (error) {
      setTotpError(error instanceof Error ? error.message : "生成动态码密钥失败");
    } finally {
      setBusy("");
    }
  }

  async function confirmSetup(event: FormEvent) {
    event.preventDefault();
    setBusy("confirm");
    setTotpError("");
    try {
      const result = await client.confirmTotpSetup(setupCode);
      setSetup(null);
      setSetupCode("");
      setRecoveryCodes(result.recoveryCodes);
      setToast("Google 动态码已启用");
      reload();
      window.dispatchEvent(new CustomEvent("admin-profile-refresh"));
    } catch (error) {
      setTotpError(error instanceof Error ? error.message : "动态码确认失败");
    } finally {
      setBusy("");
    }
  }

  async function regenerateCodes(event: FormEvent) {
    event.preventDefault();
    setBusy("regenerate");
    setTotpError("");
    try {
      const result = await client.regenerateRecoveryCodes(regenerateForm);
      setRecoveryCodes(result.recoveryCodes);
      setRegenerateForm({ currentPassword: "", code: "" });
      setToast("恢复码已重置");
      reload();
      window.dispatchEvent(new CustomEvent("admin-profile-refresh"));
    } catch (error) {
      setTotpError(error instanceof Error ? error.message : "恢复码重置失败");
    } finally {
      setBusy("");
    }
  }

  async function disable(event: FormEvent) {
    event.preventDefault();
    if (!window.confirm("确定停用 Google 动态码吗？停用后后台会要求重新绑定动态码。")) return;
    setBusy("disable");
    setTotpError("");
    try {
      await client.disableTotp(disableForm);
      setDisableForm({ currentPassword: "", code: "" });
      setRecoveryCodes([]);
      setSetup(null);
      setToast("Google 动态码已停用");
      reload();
      window.dispatchEvent(new CustomEvent("admin-profile-refresh"));
    } catch (error) {
      setTotpError(error instanceof Error ? error.message : "停用动态码失败");
    } finally {
      setBusy("");
    }
  }

  async function copyText(value: string, message: string) {
    await navigator.clipboard?.writeText(value);
    setToast(message);
  }

  return (
    <section className="panel narrow">
      <div className="panel-head">
        <h2>Google 动态码</h2>
        <span className={`badge ${security.totpEnabled ? "ready" : "needs_review"}`}>
          {security.totpEnabled ? "已启用" : "未启用"}
        </span>
      </div>
      <div className="settings-form">
        <StatusLine label="绑定状态" value={security.totpEnabled ? "已绑定" : "待绑定"} />
        <StatusLine label="绑定时间" value={shortDate(security.totpConfirmedAt)} />
        <StatusLine label="剩余恢复码" value={security.recoveryCodesRemaining} />
        {!security.totpEnabled && (
          <div className="security-note warning">
            <strong>必须绑定动态码后才能使用后台功能</strong>
            <span>生成密钥后，在 Google Authenticator 中选择手动输入密钥，再用 6 位动态码确认。</span>
          </div>
        )}
        {!security.totpEnabled && !setup && (
          <button className="primary" disabled={busy === "setup"} onClick={startSetup}>
            {busy === "setup" ? <Loader2 className="spin" size={17} /> : <ShieldCheck size={17} />}
            生成绑定密钥
          </button>
        )}
        {setup && (
          <form className="settings-form setup-box" onSubmit={confirmSetup}>
            <label>手动输入密钥
              <div className="copy-row">
                <input value={setup.secret} readOnly />
                <button type="button" className="icon-button" title="复制密钥" aria-label="复制密钥" onClick={() => copyText(setup.secret, "密钥已复制")}><Copy size={16} /></button>
              </div>
            </label>
            <label>otpauth 链接
              <div className="copy-row">
                <input value={setup.otpauthUri} readOnly />
                <button type="button" className="icon-button" title="复制链接" aria-label="复制链接" onClick={() => copyText(setup.otpauthUri, "otpauth 链接已复制")}><Copy size={16} /></button>
              </div>
            </label>
            <label>6 位动态码<input value={setupCode} inputMode="numeric" autoComplete="one-time-code" onChange={(event) => setSetupCode(event.target.value)} /></label>
            <button className="primary" disabled={busy === "confirm"}>
              {busy === "confirm" ? <Loader2 className="spin" size={17} /> : <CheckCircle2 size={17} />}
              确认启用动态码
            </button>
          </form>
        )}
        {recoveryCodes.length > 0 && (
          <div className="recovery-box">
            <div className="panel-head">
              <h2>恢复码</h2>
              <button type="button" className="secondary" onClick={() => copyText(recoveryCodes.join("\n"), "恢复码已复制")}><Copy size={16} />复制</button>
            </div>
            <div className="recovery-grid">
              {recoveryCodes.map((code) => <code key={code}>{code}</code>)}
            </div>
            <p className="form-hint">恢复码只显示这一次，每个只能使用一次。</p>
          </div>
        )}
        {security.totpEnabled && (
          <form className="settings-form setup-box" onSubmit={regenerateCodes}>
            <h2>重置恢复码</h2>
            <label>当前密码<input type="password" value={regenerateForm.currentPassword} onChange={(event) => setRegenerateForm({ ...regenerateForm, currentPassword: event.target.value })} /></label>
            <label>动态码 / 恢复码<input value={regenerateForm.code} onChange={(event) => setRegenerateForm({ ...regenerateForm, code: event.target.value })} /></label>
            <button className="secondary" disabled={busy === "regenerate"}>
              {busy === "regenerate" ? <Loader2 className="spin" size={17} /> : <RefreshCw size={17} />}
              生成新的恢复码
            </button>
          </form>
        )}
        {security.totpEnabled && (
          <form className="settings-form setup-box" onSubmit={disable}>
            <h2>停用动态码</h2>
            <label>当前密码<input type="password" value={disableForm.currentPassword} onChange={(event) => setDisableForm({ ...disableForm, currentPassword: event.target.value })} /></label>
            <label>动态码 / 恢复码<input value={disableForm.code} onChange={(event) => setDisableForm({ ...disableForm, code: event.target.value })} /></label>
            <button className="danger" disabled={busy === "disable"}>
              {busy === "disable" ? <Loader2 className="spin" size={17} /> : <AlertTriangle size={17} />}
              停用 Google 动态码
            </button>
          </form>
        )}
        {totpError && <div className="form-error">{totpError}</div>}
      </div>
    </section>
  );
}

function ExportsPage({ setToast, initialMonth }: { setToast: (value: string) => void; initialMonth: string }) {
  const [month, setMonth] = useState(initialMonth);
  const [generating, setGenerating] = useState(false);
  const [retryingOcr, setRetryingOcr] = useState(false);
  const [includeA4Layout, setIncludeA4Layout] = useState(true);
  const { data, reload, loading } = useLoad(() => client.exports(), []);
  const visibleExports = useMemo(
    () => (data?.items || []).filter((record) => record.period_month === month),
    [data, month]
  );
  const hasActiveExport = visibleExports.some((record) => record.status === "queued" || record.status === "processing");

  useEffect(() => {
    setMonth(initialMonth);
  }, [initialMonth]);

  function changeMonth(value: string) {
    setMonth(value);
    reload();
  }

  useEffect(() => {
    if (!hasActiveExport) return;
    const timer = window.setInterval(() => reload(), 5000);
    return () => window.clearInterval(timer);
  }, [hasActiveExport, reload]);

  async function generate() {
    if (!month || generating) return;
    setGenerating(true);
    try {
      await client.generateExport(month, includeA4Layout);
      setToast(`${month} 会计包已开始处理，请稍后刷新或稍后再下载`);
      reload();
    } catch (error) {
      setToast(error instanceof Error ? error.message : "生成会计包失败");
    } finally {
      setGenerating(false);
    }
  }

  async function retryMonthOcr() {
    if (!month || retryingOcr) return;
    setRetryingOcr(true);
    try {
      const result = await client.retryMonthOcr(month);
      setToast(`${month} 已重新排队 ${result.queued} 张识别失败票据`);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "重新识别失败");
    } finally {
      setRetryingOcr(false);
    }
  }

  return (
    <section className="panel full">
      <div className="panel-head export-toolbar">
        <label className="month-control">
          <span className="filter-label">开票月份</span>
          <input type="month" value={month} onChange={(event) => changeMonth(event.target.value)} />
        </label>
        <div className="export-options">
          <label className="inline-check">
            <input type="checkbox" checked={includeA4Layout} onChange={(event) => setIncludeA4Layout(event.target.checked)} />
            包含 A4 两张拼版
          </label>
        </div>
        <div className="export-actions">
          <button className="primary" disabled={!month || generating} onClick={generate}>
            {generating ? <Loader2 className="spin" size={17} /> : <FileArchive size={17} />}
            生成本月会计包
          </button>
          <button className="secondary" disabled={!month || retryingOcr} onClick={retryMonthOcr}>
            {retryingOcr ? <Loader2 className="spin" size={17} /> : <RefreshCw size={17} />}
            重试本月识别失败
          </button>
        </div>
      </div>
      {loading ? <PanelLoading /> : (
        <table>
          <thead><tr><th>开票月份</th><th>可入账数量</th><th>A4 已拼接</th><th>未拼接</th><th>说明</th><th>状态</th><th>保留至</th><th>生成时间</th><th></th></tr></thead>
          <tbody>
            {visibleExports.length === 0 ? (
              <tr>
                <td className="empty-cell" colSpan={9}>{month ? `${month} 尚未生成会计包` : "请选择开票月份"}</td>
              </tr>
            ) : visibleExports.map((record) => (
              <tr key={record.id}>
                <td>{record.period_month}</td>
                <td>{record.record_count}</td>
                <td>{record.a4_layout_count || 0}</td>
                <td>{record.a4_unprocessed_count || 0}</td>
                <td>{exportDescription(record)}</td>
                <td><Badge value={record.storage_status === "deleted" ? "deleted" : record.status} /></td>
                <td>{shortDate(record.retention_delete_after)}</td>
                <td>{shortDate(record.created_at)}</td>
                <td className="row-actions">
                  {record.status === "ready" ? (
                    <>
                      <a className="table-action" title="下载总览 Excel" aria-label="下载总览 Excel" href={client.exportFileUrl(record.id, "excel", `${record.period_month}_总览.xlsx`)}><FileText size={16} />总览 Excel</a>
                      <a className="table-action" title="下载本月会计包 ZIP" aria-label="下载本月会计包 ZIP" href={client.exportFileUrl(record.id, "zip", `${record.period_month}_发票会计包.zip`)}><Download size={16} />会计包 ZIP</a>
                    </>
                  ) : (
                    <span className="muted-action">{record.status === "failed" ? record.error_message || "生成失败" : "正在处理中，请稍后"}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function LogsPage() {
  const { data, loading } = useLoad(() => client.logs(), []);
  return (
    <section className="panel full">
      {loading ? <PanelLoading /> : (
        <table>
          <thead><tr><th>时间</th><th>操作者</th><th>动作</th><th>对象</th></tr></thead>
          <tbody>
            {data?.items.map((log: AuditLog) => (
              <tr key={log.id}>
                <td>{shortDate(log.created_at)}</td>
                <td>{log.actor}</td>
                <td>{log.action}</td>
                <td>{log.target_type} #{log.target_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Metric({ title, value, tone }: { title: string; value: string | number; tone?: "warn" | "muted" }) {
  return <div className={`metric ${tone || ""}`}><span>{title}</span><strong>{value}</strong></div>;
}

function StatusLine({ label, value }: { label: string; value: string | number }) {
  return <div className="status-line"><span>{label}</span><strong>{value}</strong></div>;
}

function Badge({ value }: { value: string }) {
  return <span className={`badge ${value}`}>{statusLabel(value)}</span>;
}

function LoadingScreen() {
  return <div className="loading-screen"><Loader2 className="spin" size={28} /></div>;
}

function PanelLoading() {
  return <div className="panel-loading"><Loader2 className="spin" size={22} />加载中</div>;
}

function useLoad<T>(loader: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    loader()
      .then((result) => alive && setData(result))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [...deps, version]);
  return { data, loading, reload: () => setVersion((value) => value + 1) };
}

function parseAliases(value: string) {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function money(value: number) {
  return Number(value || 0).toLocaleString("zh-CN", { style: "currency", currency: "CNY" });
}

function imageProcessLabel(invoice: Invoice) {
  if (!invoice.mime_type?.startsWith("image/")) return "不支持";
  const status = invoice.image_process_status || "pending";
  if (status === "succeeded") return "成功";
  if (status === "failed") return "失败";
  if (status === "unsupported") return "不支持";
  if (status === "processing") return "处理中";
  return "待裁切";
}

function invoiceStatusLabel(value?: string | null) {
  if (value === "ready") return "可入账";
  if (value === "ocr_pending") return "待识别";
  if (value === "ocr_failed") return "识别失败";
  if (value === "needs_review") return "待确认";
  if (value === "company_unassigned") return "未分配公司";
  if (value === "ignored") return "已删除";
  if (value === "duplicate") return "重复";
  if (value === "exported") return "已导出";
  if (value === "received") return "已接收";
  return value || "-";
}

function statusLabel(value?: string | null) {
  const invoiceLabel = invoiceStatusLabel(value);
  if (invoiceLabel !== (value || "-")) return invoiceLabel;

  if (value === "queued") return "已排队";
  if (value === "processing") return "处理中";
  if (value === "pending") return "待处理";
  if (value === "running") return "运行中";
  if (value === "succeeded") return "成功";
  if (value === "failed") return "失败";
  if (value === "unsupported") return "不支持";
  if (value === "deleted") return "已清理";
  if (value === "active") return "有效";
  if (value === "enabled") return "启用";
  if (value === "disabled") return "停用";
  return value || "-";
}

function environmentLabel() {
  const host = window.location.hostname;
  if (host === "hub.jinglei.online") return "个人工作中心 · 正式环境";
  if (host === "hub-test.jinglei.online") return "个人工作中心 · 测试环境";
  return "个人工作中心 · 本地环境";
}

function exportDescription(record: ExportRecord) {
  if (record.status === "failed") return record.error_message || "生成失败";
  if (record.status === "queued") return "已排队，等待处理";
  if (record.status === "processing") return "正在处理中，请稍后";
  if (record.include_a4_layout) return "包含单张发票和 A4 拼版";
  return record.record_count > 0 ? "包含单张发票" : "只含总览";
}

function sourceLabel(value?: string) {
  if (value === "wecom") return "企业微信";
  if (value === "admin_upload") return "补录";
  return value || "-";
}

function documentTypeLabel(invoice: Invoice) {
  if (invoice.document_type === "voucher") return voucherTypeLabel(invoice.voucher_type);
  return "标准发票";
}

function voucherTypeLabel(value?: string | null) {
  if (value === "social_security") return "社保";
  if (value === "medical_insurance") return "医保";
  if (value === "payment_receipt") return "回单/缴费凭证";
  if (value === "other_voucher") return "其他凭证";
  return "非发票凭证";
}

function directionLabel(value?: string | null) {
  if (value === "input") return "进项";
  if (value === "output") return "销项";
  return "未判断";
}

function shortDate(value?: string | null) {
  if (!value) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(hasTimezone ? normalized : `${normalized}Z`);
  if (Number.isNaN(date.getTime())) return value.replace("T", " ").slice(0, 16);

  const pad = (number: number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
